import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Sparkles, Gift, Menu, X, Search as SearchIcon, Instagram } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings, useInstagram } from "@/context/SettingsContext";
import { motion, AnimatePresence } from "framer-motion";

export const Header = () => {
  const { count, setIsOpen } = useCart();
  const settings = useSettings();
  const ig = useInstagram();
  // Ordered nav; Corporate & Custom Silver appear unless disabled in Admin; Contact stays last.
  const links = React.useMemo(() => {
    const arr = [
      { to: "/shop", label: "Explore Gifts" },
      { to: "/build", label: "Build Your Parcel" },
    ];
    if (settings?.corporate_enabled !== false) arr.push({ to: "/corporate-gifting", label: "Corporate Gifting" });
    arr.push({ to: "/our-story", label: "Our Story" });
    arr.push({ to: "/track", label: "Track Order" });
    if (settings?.custom_silver_enabled !== false) arr.push({ to: "/custom-silver", label: "Custom Silver Gifts" });
    arr.push({ to: "/contact", label: "Contact" });
    return arr;
  }, [settings?.corporate_enabled, settings?.custom_silver_enabled]);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const announcement = settings?.announcement;

  return (
    <div className="sticky top-0 z-50">
      {announcement && (
        <div className="bg-wp-rose-strong text-center text-xs font-medium text-white">
          <div className="wp-container py-1.5">{announcement}</div>
        </div>
      )}
      <header className={`transition-[background-color,box-shadow,backdrop-filter] duration-300 ${scrolled ? "glass-strong shadow-wp-soft" : "bg-wp-bg/80 backdrop-blur"}`}>
        <div className="wp-container flex h-16 items-center justify-between gap-3">
          <Link to="/" data-testid="header-logo" className="flex flex-shrink-0 items-center gap-2.5">
            <img src="/logo.jpg" alt="Wonder Parcel" className="h-11 w-11 rounded-full object-cover shadow-wp-soft ring-1 ring-wp-beige-200" />
            <span className="flex flex-col leading-none">
              <span className="font-display text-lg leading-none tracking-[0.01em] text-wp-rose-strong">Wonder</span>
              <span className="wp-label mt-0.5 text-[11px] leading-none tracking-[0.2em] text-wp-ink-muted">Parcel</span>
            </span>
          </Link>

          {/* Desktop search */}
          <form onSubmit={doSearch} className="hidden max-w-xs flex-1 xl:block">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-wp-ink-muted" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search gifts…"
                data-testid="header-search-input"
                className="w-full rounded-full border border-border bg-white/80 py-2 pl-9 pr-4 text-sm outline-none transition-shadow focus:ring-2 focus:ring-wp-rose/40"
              />
            </div>
          </form>

          <nav className="hidden items-center gap-4 lg:flex">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.split(" ")[0].toLowerCase()}`}
                className={`whitespace-nowrap text-sm font-medium transition-colors hover:text-wp-rose-strong ${location.pathname === l.to ? "text-wp-rose-strong" : "text-wp-ink"}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex flex-shrink-0 items-center gap-1.5">
            <Link to="/search" aria-label="Search" data-testid="header-search-mobile"
              className="grid h-10 w-10 place-items-center rounded-full bg-white/70 text-wp-ink shadow-wp-soft xl:hidden">
              <SearchIcon className="h-5 w-5" />
            </Link>
            {ig.enabled && (
              <a href={ig.url} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="header-instagram"
                className="hidden h-10 w-10 place-items-center rounded-full bg-white/70 text-wp-rose-strong shadow-wp-soft transition-transform hover:scale-105 sm:grid">
                <Instagram className="h-5 w-5" />
              </a>
            )}
            <button onClick={() => setIsOpen(true)} aria-label="Open cart" data-testid="cart-drawer-open-button"
              className="relative grid h-10 w-10 place-items-center rounded-full bg-white/70 text-wp-ink shadow-wp-soft transition-transform hover:scale-105 active:scale-95">
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-wp-rose-strong px-1 text-[11px] font-semibold text-white">{count}</span>
              )}
            </button>
            <button onClick={() => setMenuOpen((v) => !v)} aria-label="Menu" data-testid="header-menu-button"
              className="grid h-10 w-10 place-items-center rounded-full bg-white/70 text-wp-ink shadow-wp-soft lg:hidden">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden border-t border-border glass-strong lg:hidden">
              <div className="wp-container flex flex-col py-2">
                {links.map((l) => (
                  <button key={l.to} onClick={() => navigate(l.to)} data-testid={`mobilenav-${l.label.split(" ")[0].toLowerCase()}`}
                    className="py-3 text-left text-base font-medium text-wp-ink">{l.label}</button>
                ))}
                {ig.enabled && (
                  <a href={ig.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 py-3 text-base font-medium text-wp-rose-strong">
                    <Instagram className="h-5 w-5" /> Follow on Instagram
                  </a>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
};
